/**
 * Step 2 config transform: API → domain mapping for frontend.
 *
 * - API `unit_of_analysis` → Unit of Analysis: top-level entity selection (e.g. Company, Geography, Occupation).
 * - API `filters` (on deliverable) → General Filters: filters that apply to the whole deliverable (date range, custom filters).
 * - API `aggregation_columns` → Aggregation Columns: selectable columns for how data is grouped/sliced.
 * - API `files` → Breakdowns: further datasets or breakdowns; each has columns and can optionally have filters.
 */
import {
  type AggregationColumnItem,
  type BooleanFilterDefinition,
  type ColumnOption,
  type CustomFilterCategory,
  type CustomFilterDefinition,
  type DateFilterDefinition,
  type FilterModifier,
  getAggregationColumnItems,
  getAggregationColumnOptions,
  getAggregationSupportsCustomColumns,
  getCustomFilters,
  getDeliverableHasGeneralFilters,
  getDeliverableSettings,
  getFileSections,
  getUnitOfAnalysisOptions,
  type NumericFilterDefinition,
  type SelectionListFilterDefinition,
  type StringFilterDefinition,
  type UnitOfAnalysisOption,
} from '../api/columns-api-helpers';
import type {
  ApiDeliverable,
  ApiSetting,
  DataBuilderRawResponse,
} from '../api/columns-api-types';
import type { DataBuilderCondition } from '../api/data-builder-api.types';

export type {
  AggregationColumnItem,
  BooleanFilterDefinition,
  ColumnOption,
  CustomFilterCategory,
  CustomFilterDefinition,
  DateFilterDefinition,
  FilterModifier,
  NumericFilterDefinition,
  SelectionListFilterDefinition,
  StringFilterDefinition,
  UnitOfAnalysisOption,
};

export type UnitOfAnalysis = {
  options: UnitOfAnalysisOption[];
};

export type GeneralFilters = {
  supportsFilters: boolean;
  customFilterDefinitions: CustomFilterDefinition[];
  customFilterCategories: CustomFilterCategory[];
};

export type AggregationColumns = {
  condition?: DataBuilderCondition | null;
  options: ColumnOption[];
  items: AggregationColumnItem[];
  supportsCustomColumns: boolean;
  customColumnFilterDefinitions: CustomFilterDefinition[];
  customColumnFilterCategories: CustomFilterCategory[];
};

export type Breakdown = {
  id: string;
  label: string;
  isDefault: boolean;
  condition?: DataBuilderCondition | null;
  columnOptions: ColumnOption[];
  columnItems: AggregationColumnItem[];
  supportsFilters: boolean;
  customFilterDefinitions: CustomFilterDefinition[];
  customFilterCategories: CustomFilterCategory[];
  supportsCustomColumns: boolean;
  customColumnFilterDefinitions: CustomFilterDefinition[];
  customColumnFilterCategories: CustomFilterCategory[];
};

export type DeliverableSetting = ApiSetting;

export type DeliverableColumnConfig = {
  datasetKey: string;
  settings: DeliverableSetting[];
  unitOfAnalysis: UnitOfAnalysis;
  generalFilters: GeneralFilters;
  aggregationColumns: AggregationColumns;
  breakdowns: Breakdown[];
};

type ApiDeliverableGroup = {
  type?: string | null;
  deliverables?: ApiDeliverable[];
};

const excludeSeparateDefs = (
  defs: CustomFilterDefinition[]
): CustomFilterDefinition[] => defs.filter((d) => !d.isSeparate);

const flattenDeliverables = (
  items: (ApiDeliverable | ApiDeliverableGroup)[] | undefined
): ApiDeliverable[] => {
  if (!Array.isArray(items)) {
    return [];
  }

  const deliverables: ApiDeliverable[] = [];
  for (const item of items) {
    if ('deliverables' in item && Array.isArray(item.deliverables)) {
      deliverables.push(...item.deliverables);
      continue;
    }

    if ('key' in item && Array.isArray(item.files)) {
      deliverables.push(item);
    }
  }

  return deliverables;
};

export function buildDeliverablesConfig(
  raw: DataBuilderRawResponse | undefined
): Record<string, DeliverableColumnConfig> {
  const deliverables = flattenDeliverables(
    raw?.deliverables as (ApiDeliverable | ApiDeliverableGroup)[] | undefined
  );
  if (deliverables.length === 0) {
    return {};
  }
  const out: Record<string, DeliverableColumnConfig> = {};
  for (const d of deliverables) {
    if (d.key) {
      const fileSections = getFileSections(d);
      const {
        definitions: deliverableCustomFilterDefinitions,
        categories: deliverableCustomFilterCategories,
      } = getCustomFilters(d);
      const deliverableCustomColumnFilterDefinitions = excludeSeparateDefs(
        deliverableCustomFilterDefinitions
      );
      out[d.key] = {
        datasetKey: d.key,
        settings: getDeliverableSettings(d),
        unitOfAnalysis: {
          options: getUnitOfAnalysisOptions(d),
        },
        generalFilters: {
          supportsFilters: getDeliverableHasGeneralFilters(d),
          customFilterDefinitions: deliverableCustomFilterDefinitions,
          customFilterCategories: deliverableCustomFilterCategories,
        },
        aggregationColumns: {
          condition: d.aggregation_columns?.condition,
          options: getAggregationColumnOptions(d),
          items: getAggregationColumnItems(d),
          supportsCustomColumns: getAggregationSupportsCustomColumns(d),
          customColumnFilterDefinitions:
            deliverableCustomColumnFilterDefinitions,
          customColumnFilterCategories: deliverableCustomFilterCategories,
        },
        breakdowns: fileSections.map((f) => {
          const useFileFilters = f.customFilterDefinitions.length > 0;
          return {
            id: f.id,
            label: f.label,
            isDefault: f.isDefault,
            condition: f.condition,
            columnOptions: f.columnOptions,
            columnItems: f.columnItems,
            supportsFilters: f.hasFilters,
            customFilterDefinitions: f.customFilterDefinitions,
            customFilterCategories: f.customFilterCategories,
            supportsCustomColumns: f.supportsCustomColumns,
            customColumnFilterDefinitions: useFileFilters
              ? excludeSeparateDefs(f.customFilterDefinitions)
              : deliverableCustomColumnFilterDefinitions,
            customColumnFilterCategories: useFileFilters
              ? f.customFilterCategories
              : deliverableCustomFilterCategories,
          };
        }),
      };
    }
  }

  return out;
}

export function getDeliverableColumnConfig(
  configs: Record<string, DeliverableColumnConfig>,
  deliverableKey: string | null
): DeliverableColumnConfig | undefined {
  if (deliverableKey === null || deliverableKey === '') {
    return undefined;
  }
  return configs[deliverableKey];
}
